import { useMutation } from '@redwoodjs/web';

import { showErrorToast } from 'src/components/Toasts/toast';
import {
    VALIDATE_BIC,
    VALIDATE_IBAN,
    VALIDATE_UK_BANK,
} from 'src/graphql/mutations/bank-validation.mutation';
import { hasInvalidChars } from 'src/utils/utils';
import { UKBankDetails } from 'types/graphql';

const useBankValidation = () => {
    const [validateIBAN, { loading: validatingIBAN }] = useMutation(VALIDATE_IBAN);
    const [validateBIC, { loading: validatingBIC }] = useMutation(VALIDATE_BIC);
    const [validateUKBank, { loading: validatingUKBank }] = useMutation(VALIDATE_UK_BANK);

    const onValidateIBAN = async (
        IBAN: string,
        onSuccess?: (isValid: boolean) => void,
        onError?: (message: string) => void
    ) => {
        if (hasInvalidChars(IBAN)) {
            onError && onError('IBAN is not valid');
            return;
        }
        await validateIBAN({
            variables: {
                IBAN,
            },
            onCompleted: ({ validateIBAN }) => {
                if (validateIBAN) {
                    onSuccess && onSuccess(validateIBAN);
                } else {
                    onError && onError('IBAN is not valid');
                }
            },
            onError: () => {
                onError && onError('Error validating IBAN');
            },
        });
    };

    const onValidateBIC = async (
        input: {
            IBAN: string;
            BIC: string;
        },
        onSuccess?: (isValid: boolean) => void,
        onError?: (message: string) => void
    ) => {
        if (hasInvalidChars(input.IBAN)) {
            onError && onError('IBAN is not valid');
            return;
        }

        if (hasInvalidChars(input.BIC)) {
            onError && onError('BIC is not valid');
            return;
        }
        await validateBIC({
            variables: {
                ...input,
            },
            onCompleted: ({ validateBIC }) => {
                if (validateBIC) {
                    onSuccess && onSuccess(validateBIC);
                } else {
                    onError && onError('BIC is not valid');
                }
            },
            onError: (err) => {
                onError && onError(err.message);
            },
        });
    };

    const validateIBANAndBIC = async (
        iban: string,
        bic: string,
        ibanField: string,
        bicField: string
    ) => {
        const errors: Record<string, string> = {};
        const isValidIBAN = await new Promise((resolve) => {
            if (hasInvalidChars(iban)) {
                showErrorToast('Invalid IBAN');
                return resolve(false);
            }

            return onValidateIBAN(
                iban,
                async () => {
                    const isValidBIC = await new Promise((resolve) => {
                        if (hasInvalidChars(bic)) {
                            showErrorToast('Invalid BIC');
                            return resolve(false);
                        }
                        return onValidateBIC({ BIC: bic, IBAN: iban }, resolve, (err) => {
                            showErrorToast(err);
                            return resolve(false);
                        });
                    });
                    if (!isValidBIC) {
                        errors[bicField] = 'Invalid BIC';
                    }
                    resolve(true);
                },
                (err) => {
                    showErrorToast(err);
                    errors[ibanField] = 'Invalid IBAN';
                    return resolve(false);
                }
            );
        });

        if (!isValidIBAN) {
            errors[ibanField] = 'Invalid IBAN';
        }

        return errors;
    };

    const onValidateUKBank = async (
        input: {
            accountNumber: string;
            sortCode: string;
        },
        onSuccess?: (data: UKBankDetails) => void,
        onError?: (message: string) => void
    ) => {
        await validateUKBank({
            variables: {
                ...input,
            },
            onCompleted: ({ validateUKBank }) => {
                if (validateUKBank) {
                    onSuccess && onSuccess(validateUKBank);
                } else {
                    onError && onError('Bank details are not valid');
                }
            },
            onError: (err) => {
                onError && onError(err.message);
            },
        });
    };

    return {
        onValidateIBAN,
        validatingIBAN,
        onValidateBIC,
        validatingBIC,
        validateIBANAndBIC,
        onValidateUKBank,
        validatingUKBank,
    };
};
export default useBankValidation;
